import type {
  SettingItemConfig,
  SettingItemConfigId,
  SettingsConfig,
} from './settingsConfigSchema.ts'

/**
 * {
 *   [itemConfigId]: <itemConfig>,
 *   ...
 * }
 */
export type GenericFlattenedSettingItemConfig = Record<
  SettingItemConfigId,
  SettingItemConfig
>

const itemConfigByIdMapCache = new Map<
  SettingsConfig['settingsId'],
  GenericFlattenedSettingItemConfig
>()

/**
 * Compose a map of all setting item configs by itemId.
 * This is used to get the config of a setting item by itemId directly
 * by eliminating the sections.
 *
 * The data returned is intentionally widened because it will be used in
 * generic functions which generic variables are not resolved yet.
 *
 * If we were to make this stricter by inferring each type of each key,
 * we would have to use type assert to widen it in those generic functions anyway.
 */
export const composeSettingItemConfigByIdMap = <
  RootConfig extends SettingsConfig,
>(
  settingsConfig: RootConfig,
): Readonly<GenericFlattenedSettingItemConfig> => {
  const cached = itemConfigByIdMapCache.get(settingsConfig.settingsId)
  if (cached) return cached

  const itemConfigByIdMap: GenericFlattenedSettingItemConfig = {}

  Object.values(settingsConfig.sections).forEach((section) => {
    Object.entries(section.items).forEach(([itemId, itemConfig]) => {
      itemConfigByIdMap[itemId] = itemConfig
    })
  })

  itemConfigByIdMapCache.set(settingsConfig.settingsId, itemConfigByIdMap)

  // Prevent mutating the map object directly.
  return Object.freeze(itemConfigByIdMap)
}
